import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  actions,
  InfoWorkType,
  InterestInCredit,
  MaxBudget,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import { useApiError, useAppDispatch } from 'hooks';
import { useCallback } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useEnquiryContext } from '../context';

export interface EnquiryProjectBudgetFormValues {
  interest_in_credit: InterestInCredit;
  budget: string;
  budgets: MaxBudget;
}

export interface EnquiryProjectBudgetFormHook {
  form: UseFormReturn<EnquiryProjectBudgetFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<EnquiryProjectBudgetFormValues>,
  ) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  infoWorkTypes: InfoWorkType[] | undefined;
  workType: WorkType | undefined;
}

export default function useEnquiryProjectBudgetForm(): EnquiryProjectBudgetFormHook {
  const {
    isLoading,
    isSubmitting,
    setIsSubmitting,
    id,
    infoWorkTypes,
    workType,
  } = useEnquiryContext();
  const dispatch = useAppDispatch();
  const onApiError = useApiError('Unknown error submitting enquiry budget');
  const { push } = useHistory();
  const { showSuccess } = useToast();

  const form = useForm<EnquiryProjectBudgetFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<EnquiryProjectBudgetFormValues>) => {
      if (!id) return;
      setIsSubmitting(true);
      const { budgets, interest_in_credit: interestInCredit } =
        formValues as unknown as EnquiryProjectBudgetFormValues;
      if (!budgets || !interestInCredit) return;
      dispatch(
        actions.postEnquiryInterestInCredit({
          body: { interest_in_credit: interestInCredit },
          params: { id },
          onError: (err) => {
            setIsSubmitting(false);
            onApiError(err);
          },
          onSuccess: () => {
            dispatch(
              actions.postEnquiryBudget({
                body: budgets,
                params: { id },
                onError: (err) => {
                  setIsSubmitting(false);
                  onApiError(err);
                },
                onSuccess: () => {
                  dispatch(
                    actions.postEnquirySubmit({
                      params: { id },
                      onError: (err) => {
                        setIsSubmitting(false);
                        onApiError(err);
                      },
                      onSuccess: () => {
                        setIsSubmitting(false);
                        showSuccess('Enquiry budget set');
                        push(generatePath(URLS.enquiryComplete, { id }));
                      },
                    }),
                  );
                },
              }),
            );
          },
        }),
      );
    },
    [dispatch, id, onApiError, showSuccess, push, setIsSubmitting],
  );

  return {
    form,
    onSubmit,
    isLoading,
    isSubmitting,
    infoWorkTypes,
    workType,
  };
}
